import React, { useState } from 'react';
import {
  MapIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  StarIcon,
  TagIcon,
  WrenchScrewdriverIcon,
  GlobeAltIcon,
  PhotoIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  DocumentTextIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const GettingStartedGuide = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: 'map',
      title: 'Using the Interactive Map',
      icon: MapIcon,
      summary: 'Draw and manage cemetery plots visually on an interactive map.',
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Map</strong> is the central feature of the application, allowing you to visually manage your cemetery grounds. Access the full map by clicking <strong>"Map"</strong> in the navigation bar.
          </p>

          <h4 className="font-medium text-gray-900">Drawing New Plots</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Click the <strong>Draw</strong> button in the map toolbar to enter drawing mode</li>
            <li>Click on the map to place points defining the corners of your plot</li>
            <li>Complete the shape by clicking on the starting point or double-clicking</li>
            <li>A form will appear to add plot details like alias and section</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Plot Organization</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Alias:</strong> A unique identifier for the plot (e.g., "A-101", "North-15")</li>
            <li><strong>Section:</strong> Group plots by cemetery section (e.g., "Section A", "Garden of Roses")</li>
            <li><strong>Size:</strong> Record the plot dimensions for reference</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Understanding Plot Colors</h4>
          <p>Plots are color-coded based on their status:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Gray:</strong> Vacant plots with no contacts assigned</li>
            <li><strong>Green:</strong> Reserved plots with contacts but no one interred</li>
            <li><strong>Blue:</strong> Occupied plots where someone has been interred</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Selecting and Editing Plots</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Click on any plot to select it and view its details</li>
            <li>Use the "View on Map" feature from contacts or sales to highlight specific plots</li>
            <li>Edit plot details by selecting a plot and updating the information panel</li>
            <li>Upload photos, documents, and links via the <strong>Attachments</strong> section</li>
            <li>Add custom field data in the <strong>Custom Fields</strong> section (if configured)</li>
          </ul>
        </div>
      )
    },
    {
      id: 'contacts',
      title: 'Managing Contacts',
      icon: UserGroupIcon,
      summary: 'Create and manage records for plot owners, family members, and interred individuals.',
      content: (
        <div className="space-y-4">
          <p>
            <strong>Contacts</strong> represent the people associated with your cemetery - plot owners, family members, and those who have been interred. Access contacts from the <strong>"Contacts"</strong> menu in the navigation bar.
          </p>

          <h4 className="font-medium text-gray-900">Creating a New Contact</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Click the <strong>"Add Contact"</strong> button on the Contacts page</li>
            <li>Fill in the contact information:
              <ul className="list-disc list-inside ml-4 mt-2">
                <li>First and last name (required)</li>
                <li>Mailing address for correspondence</li>
                <li>Email address and phone number</li>
                <li>Status to track their current state</li>
              </ul>
            </li>
            <li>Save the contact to add them to your records</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Assigning Contacts to Plots</h4>
          <p>Contacts can be linked to plots with different relationship types:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Owner:</strong> The person who owns or purchased the plot</li>
            <li><strong>Family Member:</strong> Related individuals with plot access</li>
            <li><strong>Interred:</strong> Someone laid to rest in the plot (changes plot color to blue)</li>
          </ul>
          <p className="mt-2">To assign a contact to a plot:</p>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Open the contact's detail page</li>
            <li>Click <strong>"Add Plot"</strong> in the Plots section</li>
            <li>Search for and select the plot</li>
            <li>Choose the relationship type and add any notes</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Veteran Status</h4>
          <p>
            Mark contacts as veterans to display special indicators on plots where they are interred. This helps honor and identify veteran grave sites on the map.
          </p>
        </div>
      )
    },
    {
      id: 'sales',
      title: 'Creating and Managing Sales',
      icon: CurrencyDollarIcon,
      summary: 'Track plot sales, services, and payments with detailed records.',
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Sales</strong> module helps you track all financial transactions related to plots and services. Access it from the <strong>"Sales"</strong> menu in the navigation bar.
          </p>

          <h4 className="font-medium text-gray-900">Creating a New Sale</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Click <strong>"Add Sale"</strong> on the Sales page</li>
            <li>Select a contact (the buyer/customer)</li>
            <li>Set the sale date</li>
            <li>Add line items:
              <ul className="list-disc list-inside ml-4 mt-2">
                <li>Select services from your configured list</li>
                <li>Adjust quantities and prices as needed</li>
                <li>Add notes for specific items</li>
              </ul>
            </li>
            <li>Optionally link one or more plots to the sale</li>
            <li>Save to create the sale record</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Linking Sales to Plots</h4>
          <p>
            Each sale can be associated with multiple plots. This creates a clear record of which plots were involved in each transaction, making it easy to track:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Plot purchase history</li>
            <li>Services performed at specific locations</li>
            <li>Revenue by plot or section</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Sale Statuses & Due Dates</h4>
          <p>Sales automatically track their status based on payments:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Draft:</strong> New sale with no payments</li>
            <li><strong>Partially Paid:</strong> Some payment received but balance remains</li>
            <li><strong>Paid:</strong> Full payment received</li>
            <li><strong>Overdue:</strong> Unpaid or partially paid and past due date</li>
            <li><strong>Cancelled:</strong> Manually cancelled (set by user)</li>
            <li><strong>Refunded:</strong> Payment returned (set by user)</li>
          </ul>
          <p className="mt-2">
            Each sale has a <strong>Due Date</strong> that defaults to one month after creation. Sales with remaining balances past the due date are marked as overdue.
          </p>
        </div>
      )
    },
    {
      id: 'services',
      title: 'Configuring Services',
      icon: StarIcon,
      summary: 'Set up the products and services you offer for use in sales.',
      content: (
        <div className="space-y-4">
          <p>
            <strong>Services</strong> (found under <strong>Settings &gt; Services</strong>) are the products and services you offer to customers. These can be added as line items when creating sales.
          </p>

          <h4 className="font-medium text-gray-900">Examples of Services</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Plot purchases (e.g., "Single Plot", "Family Plot")</li>
            <li>Interment services (e.g., "Standard Burial", "Cremation Interment")</li>
            <li>Maintenance packages (e.g., "Annual Care", "Flower Placement")</li>
            <li>Memorial items (e.g., "Headstone", "Bronze Marker")</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Creating a Service</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Go to <strong>Settings &gt; Services</strong></li>
            <li>Click <strong>"Add Service"</strong></li>
            <li>Enter the service details:
              <ul className="list-disc list-inside ml-4 mt-2">
                <li>Name - what the service is called</li>
                <li>Price - the standard price</li>
                <li>Description - details about the service</li>
                <li>Tags - categorize with one or more tags (optional)</li>
              </ul>
            </li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Filtering Services by Tag</h4>
          <p>
            Use the tag filter to quickly find related services. Click on any tag in the filter panel to show only services with that tag. This is especially useful when you have many services to manage.
          </p>

          <h4 className="font-medium text-gray-900 mt-4">Using Services in Sales</h4>
          <p>
            When creating a sale, you can select from your configured services. The price will auto-fill but can be adjusted for each sale if needed (for discounts, special pricing, etc.).
          </p>
        </div>
      )
    },
    {
      id: 'tags',
      title: 'Managing Service Tags',
      icon: TagIcon,
      summary: 'Create tags to categorize and organize your services.',
      content: (
        <div className="space-y-4">
          <p>
            <strong>Service Tags</strong> (found under <strong>Settings &gt; Service Tags</strong>) help you organize and categorize your services. Each service can have multiple tags, making it easy to filter and find related services.
          </p>

          <h4 className="font-medium text-gray-900">Creating Tags</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Go to <strong>Settings &gt; Service Tags</strong></li>
            <li>Click <strong>"Add Tag"</strong></li>
            <li>Enter a name for the tag (e.g., "Seasonal", "Premium", "Maintenance")</li>
            <li>Add an optional description</li>
            <li>Save the tag</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Tag Ideas</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>By type:</strong> Interment, Maintenance, Memorial, Administrative</li>
            <li><strong>By availability:</strong> Available, Seasonal, Limited, Discontinued</li>
            <li><strong>By pricing:</strong> Premium, Standard, Discounted</li>
            <li><strong>By frequency:</strong> One-time, Annual, Recurring</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Using Tags</h4>
          <p>
            Once you've created tags, you can assign them to services on the Services page. Use the tag filter to quickly show only services with specific tags, making it easy to manage large service catalogs.
          </p>
        </div>
      )
    },
    {
      id: 'data',
      title: 'Exporting and Importing Data',
      icon: ArrowDownTrayIcon,
      summary: 'Back up your data or transfer it between systems using the export/import feature.',
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Data</strong> page (found under <strong>Settings &gt; Data</strong>) allows you to export and import all your cemetery data.
          </p>

          <h4 className="font-medium text-gray-900">Exporting Data</h4>
          <p>When you export, you'll download a ZIP file containing:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>All plots with their locations and details</li>
            <li>All contacts and their information</li>
            <li>All sales and sale items</li>
            <li>Services configuration</li>
            <li>Status definitions</li>
            <li>All relationship links (plot-contact, plot-sale)</li>
            <li>Plot attachments (images, documents, links)</li>
            <li>Custom field definitions and values</li>
            <li>Work orders with attachments and notes</li>
            <li>Email templates and logs</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Benefits of Regular Exports</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Backup:</strong> Keep a secure copy of your data</li>
            <li><strong>Reporting:</strong> Open CSV files in Excel for custom reports</li>
            <li><strong>Archival:</strong> Maintain historical records</li>
            <li><strong>Migration:</strong> Move data to another system if needed</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Importing Data</h4>
          <p>
            You can import a previously exported ZIP file to restore or transfer data. The import process:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Updates existing records if they match by ID</li>
            <li>Creates new records for data that doesn't exist</li>
            <li>Preserves all relationships between records</li>
          </ul>
          <p className="mt-2 text-amber-700 bg-amber-50 p-3 rounded-md text-sm">
            <strong>Tip:</strong> Always export a backup before importing new data, in case you need to restore.
          </p>
        </div>
      )
    },
    {
      id: 'attachments',
      title: 'Plot Attachments',
      icon: PhotoIcon,
      summary: 'Upload photos, documents, and links to individual plots.',
      content: (
        <div className="space-y-4">
          <p>
            <strong>Plot Attachments</strong> let you add images, documents, and external links directly to individual plots. Access them through the <strong>"Manage Plot"</strong> panel when you click on a plot in the map.
          </p>

          <h4 className="font-medium text-gray-900">Types of Attachments</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Images:</strong> Photos of headstones, plots, flowers, or any visual documentation</li>
            <li><strong>Documents:</strong> PDFs, deeds, agreements, or other files</li>
            <li><strong>Links:</strong> External URLs to obituaries, family sites, or other resources</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Uploading Attachments</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Click on a plot on the map to select it</li>
            <li>Click <strong>"Manage Plot"</strong> to open the management panel</li>
            <li>Scroll to the <strong>Attachments</strong> section</li>
            <li>Choose the attachment type (Image, Document, or Link)</li>
            <li>For images/documents: click the upload area or drag and drop a file</li>
            <li>For links: enter the URL and a title</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Public Visibility</h4>
          <p>
            Each attachment has a <strong>"Public"</strong> toggle. When enabled, the attachment will be visible on the public portal (if the public portal has plot attachments enabled). Use this to share headstone photos publicly while keeping private documents hidden.
          </p>

          <h4 className="font-medium text-gray-900 mt-4">Managing Attachments</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Click the <strong>eye icon</strong> on images to view them full-size</li>
            <li>Click the <strong>trash icon</strong> to delete an attachment</li>
            <li>Toggle the <strong>public/private</strong> button to control visibility</li>
          </ul>
        </div>
      )
    },
    {
      id: 'custom-fields',
      title: 'Custom Fields',
      icon: AdjustmentsHorizontalIcon,
      summary: 'Define additional fields for plots, contacts, and sales to capture custom data.',
      content: (
        <div className="space-y-4">
          <p>
            <strong>Custom Fields</strong> allow you to add your own data fields to plots, contacts, and sales beyond the standard fields. Configure them under <strong>Settings &gt; Custom Fields</strong>.
          </p>

          <h4 className="font-medium text-gray-900">Available Field Types</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Text:</strong> Short text input for names, IDs, etc.</li>
            <li><strong>Number:</strong> Numeric values for measurements, quantities, etc.</li>
            <li><strong>Date:</strong> Date picker for significant dates</li>
            <li><strong>Select:</strong> Dropdown with predefined options you define</li>
            <li><strong>Textarea:</strong> Multi-line text for longer notes or descriptions</li>
            <li><strong>Checkbox:</strong> Yes/No toggle for simple flags</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Creating Custom Fields</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Go to <strong>Settings &gt; Custom Fields</strong></li>
            <li>Select the entity type (Plot, Contact, or Sale) using the tabs</li>
            <li>Click <strong>"Add Field"</strong></li>
            <li>Enter the field details:
              <ul className="list-disc list-inside ml-4 mt-2">
                <li>Label - what the field is called (e.g., "Foundation Type")</li>
                <li>Type - choose the input type</li>
                <li>Options - for Select fields, add the available choices</li>
                <li>Required - whether the field must be filled in</li>
                <li>Public - whether the field shows on the public portal</li>
              </ul>
            </li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Example Uses</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Plots:</strong> Foundation type, orientation, installation date, monument type</li>
            <li><strong>Contacts:</strong> Membership number, church affiliation, special instructions</li>
            <li><strong>Sales:</strong> Reference number, approval status, notes category</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Geospatial Import Mapping</h4>
          <p>
            When importing plot data from shapefiles, KML, or other geospatial formats, you can map source file attributes directly to your custom fields. This makes it easy to bring in existing data without losing information.
          </p>
        </div>
      )
    },
    {
      id: 'work-orders',
      title: 'Work Orders',
      icon: ClipboardDocumentListIcon,
      summary: 'Create and track maintenance tasks, funeral prep, and grounds work.',
      content: (
        <div className="space-y-4">
          <p>
            <strong>Work Orders</strong> help you manage maintenance tasks, funeral preparations, and grounds work. Access work orders from the <strong>"Work Orders"</strong> link in the navigation bar.
          </p>

          <h4 className="font-medium text-gray-900">Work Order Types</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Funeral Preparation:</strong> Tasks to prepare a plot for interment</li>
            <li><strong>Headstone Installation:</strong> Monument or marker placement</li>
            <li><strong>Repair:</strong> Fix damaged headstones, markers, or infrastructure</li>
            <li><strong>Maintenance:</strong> Routine upkeep like mowing or cleaning</li>
            <li><strong>Landscaping:</strong> Planting, tree care, or ground improvement</li>
            <li><strong>Other:</strong> Any miscellaneous tasks</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Creating a Work Order</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Go to the <strong>Work Orders</strong> page</li>
            <li>Click <strong>"Create Work Order"</strong></li>
            <li>Fill in the details:
              <ul className="list-disc list-inside ml-4 mt-2">
                <li>Title and description of the work</li>
                <li>Type and priority level</li>
                <li>Optionally assign to a specific user</li>
                <li>Optionally link to a plot and contact</li>
                <li>Set a due date if needed</li>
              </ul>
            </li>
            <li>Save to create the work order</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Managing Work Orders</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Status Updates:</strong> Change status as work progresses (e.g., New, In Progress, Completed)</li>
            <li><strong>Notes:</strong> Add notes to track progress or communicate updates</li>
            <li><strong>Photos:</strong> Upload before/after photos or document issues</li>
            <li><strong>Printing:</strong> Print work orders for field crews</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Public Status Updates</h4>
          <p>
            Each work order has a unique <strong>public link</strong> that can be shared with external parties (e.g., headstone vendors, contractors). They can view the work order details and update its status without needing an account.
          </p>

          <h4 className="font-medium text-gray-900 mt-4">Custom Statuses</h4>
          <p>
            Administrators can configure custom statuses for work orders from the Work Orders page. Each status has a name, color, and can be marked as a default (for new orders) or final (for completed/cancelled orders).
          </p>

          <h4 className="font-medium text-gray-900 mt-4">Grounds Crew Role</h4>
          <p>
            Users with the <strong>"Grounds Crew"</strong> role can view and update work orders assigned to them, but cannot create or delete them. This is ideal for field staff who need to see their assignments and update progress.
          </p>
        </div>
      )
    },
    {
      id: 'public-portal',
      title: 'Public Portal (Admin Only)',
      icon: GlobeAltIcon,
      summary: 'Share an interactive cemetery map with the public for finding grave sites.',
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Public Portal</strong> allows you to share a read-only version of your cemetery map with the public. Visitors can view plots, search for names, and find grave locations without needing an account.
          </p>

          <h4 className="font-medium text-gray-900">Enabling the Public Portal</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Go to <strong>Settings &gt; Public Portal</strong></li>
            <li>Toggle <strong>"Enable Public Access"</strong> to on</li>
            <li>Copy the public URL to share with visitors</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Visibility Settings</h4>
          <p>Control what information is visible to public visitors:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Contact Names:</strong> Show names of people associated with plots</li>
            <li><strong>Birth/Death Dates:</strong> Display date information for interred individuals</li>
            <li><strong>Veteran Status:</strong> Highlight plots with veteran interments</li>
            <li><strong>Plot Availability:</strong> Show whether plots are available for purchase</li>
            <li><strong>Plot Attachments:</strong> Show public photos, documents, and links on plots</li>
            <li><strong>Custom Fields:</strong> Display custom field data marked as public</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Contact Information</h4>
          <p>
            Add contact details that appear on the public portal so visitors know how to reach your office:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Phone number</li>
            <li>Email address</li>
            <li>Office address</li>
            <li>Office hours</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Plot Colors</h4>
          <p>
            Customize how plots appear on the public map. You can use different colors than the admin map to highlight information relevant to public visitors (e.g., available vs. sold plots).
          </p>

          <p className="mt-4 text-blue-700 bg-blue-50 p-3 rounded-md text-sm">
            <strong>Privacy Note:</strong> Only enable visibility options that are appropriate for public viewing. Personal contact details (addresses, phone numbers) of plot owners are never shown publicly.
          </p>
        </div>
      )
    },
    {
      id: 'obituaries',
      title: 'Managing Obituaries',
      icon: HeartIcon,
      summary: 'Create and share memorial pages for those laid to rest.',
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Obituaries</strong> feature allows you to create beautiful memorial pages to commemorate those interred in your cemetery. Access obituaries from the <strong>"Obituaries"</strong> menu in the navigation bar.
          </p>

          <h4 className="font-medium text-gray-900">Creating an Obituary</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Click <strong>"Create Obituary"</strong> on the Obituaries page</li>
            <li>Enter personal information (name, dates of birth and death)</li>
            <li>Add biographical details, birthplace, and residence</li>
            <li>Include service information (date, location, notes)</li>
            <li>Add memorial donation and condolence address details if applicable</li>
            <li>Upload life photos and documents using the Attachments section</li>
            <li>Optionally link to an existing contact record</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Draft and Publish Workflow</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Draft:</strong> Save your work in progress - drafts are not visible to the public</li>
            <li><strong>Published:</strong> Make the obituary available on the public portal</li>
            <li><strong>Private:</strong> Even when published, private obituaries are hidden from public view - useful for family-only memorials</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Sharing Obituaries</h4>
          <p>
            Each public obituary has a unique shareable link. When viewing a published public obituary, click the <strong>Share</strong> button to copy the link or share directly. Family members can access the obituary without needing an account.
          </p>

          <h4 className="font-medium text-gray-900 mt-4">Public Portal Integration</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Enable <strong>Obituaries Page</strong> in Public Portal Settings to show an "In Memoriam" tab</li>
            <li>Enable <strong>Show Obituary on Plot</strong> to display "View Obituary" links on plot details for interred individuals</li>
            <li>Configure which obituary sections (biography, service info, etc.) are visible to the public</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Attachments</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Upload multiple photos to create a life photo gallery</li>
            <li>Mark one photo as primary for the main display</li>
            <li>Attach documents like memorial programs or obituary PDFs</li>
            <li>Drag and drop files or click to browse</li>
          </ul>
        </div>
      )
    },
    {
      id: 'documents',
      title: 'Document Templates & Generation',
      icon: DocumentTextIcon,
      summary: 'Create reusable templates and generate professional documents like contracts, invoices, and letters.',
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Documents</strong> feature allows you to create reusable templates and generate professional PDF documents populated with data from contacts, sales, plots, and work orders. Access it from the <strong>"Documents"</strong> option in the More menu.
          </p>

          <h4 className="font-medium text-gray-900">Creating a Document Template</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Go to <strong>More &gt; Documents</strong> in the navigation</li>
            <li>Click <strong>"New Template"</strong></li>
            <li>Enter template details:
              <ul className="list-disc list-inside ml-4 mt-2">
                <li>Name - a descriptive name for the template</li>
                <li>Template Key - a unique identifier (auto-generated from name)</li>
                <li>Category - contract, invoice, receipt, letter, report, work order, or other</li>
                <li>Entity Type - which data source this template uses (sale, contact, plot, work order, or general)</li>
              </ul>
            </li>
            <li>Write your HTML content using the editor</li>
            <li>Add CSS styles to customize the document appearance</li>
            <li>Configure page settings (size, orientation, margins)</li>
            <li>Click <strong>Preview</strong> to see how the document will look with sample data</li>
            <li>Save the template</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Using Template Variables</h4>
          <p>
            Insert dynamic data into your templates using Handlebars syntax. The Variables panel on the right shows all available fields:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Global:</strong> {"{{siteName}}"}, {"{{currentDate}}"}, {"{{currentYear}}"}</li>
            <li><strong>Contact:</strong> {"{{contact.fullName}}"}, {"{{contact.address}}"}, {"{{contact.emailAddress}}"}</li>
            <li><strong>Sale:</strong> {"{{sale.total}}"}, {"{{sale.balance}}"}, {"{{sale.saleDate}}"}</li>
            <li><strong>Plot:</strong> {"{{plot.alias}}"}, {"{{plot.section}}"}, {"{{plot.size}}"}</li>
            <li><strong>Work Order:</strong> {"{{workOrder.title}}"}, {"{{workOrder.status}}"}, {"{{workOrder.dueDate}}"}</li>
          </ul>
          <p className="mt-2">
            Click any variable in the panel to insert it at your cursor position. Use helpers like {"{{formatCurrency sale.total}}"} or {"{{formatDate sale.saleDate}}"} for formatted output.
          </p>

          <h4 className="font-medium text-gray-900 mt-4">Template Categories</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Contract:</strong> Plot purchase agreements, deed transfers</li>
            <li><strong>Invoice:</strong> Bills for services and products</li>
            <li><strong>Receipt:</strong> Payment confirmations</li>
            <li><strong>Letter:</strong> Correspondence and notifications</li>
            <li><strong>Report:</strong> Summary documents</li>
            <li><strong>Work Order:</strong> Task assignments and work instructions</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Generating Documents</h4>
          <p>
            Once you have templates created, you can generate documents from various places in the application:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>From a contact's detail page to generate contact-related documents</li>
            <li>From a sale's detail page to generate invoices, receipts, or contracts</li>
            <li>From a plot's detail page to generate plot-related documents</li>
            <li>From a work order to generate work instructions</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Document Actions</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Download:</strong> Save the PDF to your computer</li>
            <li><strong>Print:</strong> Print directly from your browser</li>
            <li><strong>Email:</strong> Send the document directly to a contact's email address</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Version History</h4>
          <p>
            Templates automatically save version history. Click the <strong>Versions</strong> button when editing a template to view previous versions and restore if needed. This ensures you never lose work and can roll back changes.
          </p>

          <h4 className="font-medium text-gray-900 mt-4">Tips for Better Templates</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Use the Preview feature to test templates before generating real documents</li>
            <li>Include CSS styles for professional formatting (fonts, margins, tables)</li>
            <li>Add header and footer content for letterhead and page numbers</li>
            <li>Test with different data to ensure variables display correctly</li>
          </ul>
        </div>
      )
    },
    {
      id: 'analytics',
      title: 'Google Analytics Integration',
      icon: ChartBarIcon,
      summary: 'Track visitor activity on your site by connecting your Google Analytics account.',
      content: (
        <div className="space-y-4">
          <p>
            Parish Recorder supports Google Analytics integration so you can track how visitors use your site, including the public portal. This is configured in <strong>Site Settings</strong> under the <strong>Analytics</strong> tab.
          </p>

          <h4 className="font-medium text-gray-900">Getting Your Google Tag ID</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Go to <strong>analytics.google.com</strong> and sign in with your Google account</li>
            <li>If you don't have a Google Analytics account yet, click <strong>Start measuring</strong> and follow the setup wizard to create an account and property</li>
            <li>In the left sidebar, click the <strong>gear icon</strong> (Admin) at the bottom</li>
            <li>Under your property column, click <strong>Data Streams</strong></li>
            <li>Click <strong>Add stream</strong> &rarr; <strong>Web</strong> if you don't have one yet, and enter your site URL</li>
            <li>Once your web stream is created, you'll see a <strong>Measurement ID</strong> that starts with <strong>G-</strong> (e.g., G-ABC123DEF4)</li>
            <li>Copy that Measurement ID</li>
          </ol>

          <h4 className="font-medium text-gray-900 mt-4">Configuring in Parish Recorder</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Navigate to <strong>Site Settings</strong> from the Settings menu</li>
            <li>Click the <strong>Analytics</strong> tab in the sidebar</li>
            <li>Paste your Google Tag ID (e.g., G-ABC123DEF4) into the <strong>Google Tag ID</strong> field</li>
            <li>Click <strong>Save Changes</strong></li>
          </ol>
          <p className="text-gray-600">
            Analytics tracking will begin immediately across all pages. To disable tracking, simply clear the Google Tag ID field and save.
          </p>

          <h4 className="font-medium text-gray-900 mt-4">What You Can Track</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Public portal visits:</strong> See how many families are searching your cemetery records</li>
            <li><strong>Popular pages:</strong> Understand which sections of your site get the most traffic</li>
            <li><strong>Search patterns:</strong> Learn what visitors are looking for</li>
            <li><strong>Device breakdown:</strong> See whether visitors are on desktop or mobile</li>
            <li><strong>Geographic data:</strong> Understand where your visitors are located</li>
          </ul>

          <div className="mt-4 rounded-md bg-blue-50 p-4">
            <p className="text-sm text-blue-700">
              <strong>Tip:</strong> Google Analytics data typically takes 24-48 hours to start appearing in your dashboard after initial setup. Real-time reports are available immediately.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'settings',
      title: 'Site Settings (Admin Only)',
      icon: WrenchScrewdriverIcon,
      summary: 'Customize the application name, plot colors, and branding for your organization.',
      content: (
        <div className="space-y-4">
          <p>
            Administrators can access <strong>Site Settings</strong> from the Settings menu to customize the application for their organization.
          </p>

          <h4 className="font-medium text-gray-900">General Settings</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Site Name:</strong> Change the name displayed in the header and browser tab to match your organization (e.g., "St. Mary's Cemetery")</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Color Settings</h4>
          <p>Customize how plots appear on the map:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>No Contacts:</strong> Color for vacant, unassigned plots</li>
            <li><strong>Has Contacts:</strong> Color for reserved plots (contacts but no interments)</li>
            <li><strong>Has Interred:</strong> Color for occupied plots</li>
            <li><strong>Highlight:</strong> Color used when navigating to a specific plot</li>
          </ul>

          <h4 className="font-medium text-gray-900 mt-4">Branding (Optional)</h4>
          <p>Personalize the application with your own logos:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Header Logo:</strong> Upload a custom logo for the navigation bar (recommended: 40px height, SVG or PNG)</li>
            <li><strong>Login Page Logo:</strong> Upload a larger logo for the login screen (recommended: 256px height, PNG)</li>
          </ul>
          <p className="mt-2 text-gray-600">
            If no custom logos are uploaded, the default Parish Recorder logos will be used. You can reset to defaults at any time.
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-5 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Getting Started Guide</h2>
        <p className="mt-1 text-sm text-gray-500">
          Learn how to use all the features of this application to manage your cemetery records effectively.
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {sections.map((section) => (
          <div key={section.id} className="overflow-hidden">
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{section.title}</h3>
                  <p className="text-sm text-gray-500">{section.summary}</p>
                </div>
              </div>
              <div className="ml-4 flex-shrink-0">
                {expandedSections[section.id] ? (
                  <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </button>

            {expandedSections[section.id] && (
              <div className="px-6 pb-6">
                <div className="ml-14 pl-4 border-l-2 border-indigo-100 text-sm text-gray-700">
                  {section.content}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
        <p className="text-sm text-gray-600">
          <strong>Need help?</strong> Click on any section above to learn more about that feature.
          For quick access, use the navigation bar at the top to explore the application.
        </p>
      </div>
    </div>
  );
};

export default GettingStartedGuide;
